import * as pc from 'playcanvas';
import { createMetalMaterial, createGlowMaterial, createMaterial } from './materials.js';
import { ARENA_SHAPES } from '../config/levels.js';

// Arena entities storage
let arenaEntities = [];
let collisionObjects = [];

// Base arena size (diameter)
const BASE_ARENA_SIZE = 22;

// Create a polygon mesh (hexagon, octagon, square, etc.)
function createPolygonMesh(app, sides, radius, height) {
    const positions = [];
    const normals = [];
    const uvs = [];
    const indices = [];

    // Top face vertices (center + perimeter)
    positions.push(0, height / 2, 0); // Center top
    normals.push(0, 1, 0);
    uvs.push(0.5, 0.5);

    for (let i = 0; i <= sides; i++) {
        const angle = (i / sides) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        positions.push(x, height / 2, z);
        normals.push(0, 1, 0);
        uvs.push((x / radius + 1) / 2, (z / radius + 1) / 2);
    }

    // Top face indices
    for (let i = 1; i <= sides; i++) {
        indices.push(0, i, i + 1);
    }

    // Bottom face vertices
    const bottomStart = positions.length / 3;
    positions.push(0, -height / 2, 0); // Center bottom
    normals.push(0, -1, 0);
    uvs.push(0.5, 0.5);

    for (let i = 0; i <= sides; i++) {
        const angle = (i / sides) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        positions.push(x, -height / 2, z);
        normals.push(0, -1, 0);
        uvs.push((x / radius + 1) / 2, (z / radius + 1) / 2);
    }

    // Bottom face indices (reversed winding)
    for (let i = 1; i <= sides; i++) {
        indices.push(bottomStart, bottomStart + i + 1, bottomStart + i);
    }

    // Side faces
    const sideStart = positions.length / 3;
    for (let i = 0; i < sides; i++) {
        const angle1 = (i / sides) * Math.PI * 2;
        const angle2 = ((i + 1) / sides) * Math.PI * 2;
        const x1 = Math.cos(angle1) * radius;
        const z1 = Math.sin(angle1) * radius;
        const x2 = Math.cos(angle2) * radius;
        const z2 = Math.sin(angle2) * radius;

        // Normal for this face
        const nx = (x1 + x2) / 2;
        const nz = (z1 + z2) / 2;
        const len = Math.sqrt(nx * nx + nz * nz);
        const normalX = nx / len;
        const normalZ = nz / len;

        const base = sideStart + i * 4;

        // Four vertices per side
        positions.push(x1, height / 2, z1);
        normals.push(normalX, 0, normalZ);
        uvs.push(i / sides, 1);

        positions.push(x2, height / 2, z2);
        normals.push(normalX, 0, normalZ);
        uvs.push((i + 1) / sides, 1);

        positions.push(x2, -height / 2, z2);
        normals.push(normalX, 0, normalZ);
        uvs.push((i + 1) / sides, 0);

        positions.push(x1, -height / 2, z1);
        normals.push(normalX, 0, normalZ);
        uvs.push(i / sides, 0);

        // Two triangles per side
        indices.push(base, base + 1, base + 2);
        indices.push(base, base + 2, base + 3);
    }

    const mesh = new pc.Mesh(app.graphicsDevice);
    mesh.setPositions(positions);
    mesh.setNormals(normals);
    mesh.setUvs(0, uvs);
    mesh.setIndices(indices);
    mesh.update();

    return mesh;
}

// Create an oval/ellipse mesh
function createOvalMesh(app, radiusX, radiusZ, height, segments = 32) {
    const positions = [];
    const normals = [];
    const uvs = [];
    const indices = [];

    // Top face
    positions.push(0, height / 2, 0);
    normals.push(0, 1, 0);
    uvs.push(0.5, 0.5);

    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = Math.cos(angle) * radiusX;
        const z = Math.sin(angle) * radiusZ;
        positions.push(x, height / 2, z);
        normals.push(0, 1, 0);
        uvs.push((x / radiusX + 1) / 2, (z / radiusZ + 1) / 2);
    }

    for (let i = 1; i <= segments; i++) {
        indices.push(0, i, i + 1);
    }

    // Bottom face
    const bottomStart = positions.length / 3;
    positions.push(0, -height / 2, 0);
    normals.push(0, -1, 0);
    uvs.push(0.5, 0.5);

    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = Math.cos(angle) * radiusX;
        const z = Math.sin(angle) * radiusZ;
        positions.push(x, -height / 2, z);
        normals.push(0, -1, 0);
        uvs.push((x / radiusX + 1) / 2, (z / radiusZ + 1) / 2);
    }

    for (let i = 1; i <= segments; i++) {
        indices.push(bottomStart, bottomStart + i + 1, bottomStart + i);
    }

    // Side faces
    const sideStart = positions.length / 3;
    for (let i = 0; i < segments; i++) {
        const angle1 = (i / segments) * Math.PI * 2;
        const angle2 = ((i + 1) / segments) * Math.PI * 2;
        const x1 = Math.cos(angle1) * radiusX;
        const z1 = Math.sin(angle1) * radiusZ;
        const x2 = Math.cos(angle2) * radiusX;
        const z2 = Math.sin(angle2) * radiusZ;

        // Normal calculation for ellipse
        const nx1 = Math.cos(angle1) / radiusX;
        const nz1 = Math.sin(angle1) / radiusZ;
        const nx2 = Math.cos(angle2) / radiusX;
        const nz2 = Math.sin(angle2) / radiusZ;
        const len1 = Math.sqrt(nx1 * nx1 + nz1 * nz1);
        const len2 = Math.sqrt(nx2 * nx2 + nz2 * nz2);

        const base = sideStart + i * 4;

        positions.push(x1, height / 2, z1);
        normals.push(nx1 / len1, 0, nz1 / len1);
        uvs.push(i / segments, 1);

        positions.push(x2, height / 2, z2);
        normals.push(nx2 / len2, 0, nz2 / len2);
        uvs.push((i + 1) / segments, 1);

        positions.push(x2, -height / 2, z2);
        normals.push(nx2 / len2, 0, nz2 / len2);
        uvs.push((i + 1) / segments, 0);

        positions.push(x1, -height / 2, z1);
        normals.push(nx1 / len1, 0, nz1 / len1);
        uvs.push(i / segments, 0);

        indices.push(base, base + 1, base + 2);
        indices.push(base, base + 2, base + 3);
    }

    const mesh = new pc.Mesh(app.graphicsDevice);
    mesh.setPositions(positions);
    mesh.setNormals(normals);
    mesh.setUvs(0, uvs);
    mesh.setIndices(indices);
    mesh.update();

    return mesh;
}

// Create floor entity based on shape config
function createFloorEntity(app, name, shapeConfig, baseSize, height, material) {
    const entity = new pc.Entity(name);
    const size = baseSize * shapeConfig.size;
    const aspectRatio = shapeConfig.aspectRatio || 1.0;

    switch (shapeConfig.shape) {
        case 'square':
            entity.addComponent('render', {
                type: 'box',
                material: material
            });
            entity.setLocalScale(size, height, size);
            break;

        case 'rectangle':
            entity.addComponent('render', {
                type: 'box',
                material: material
            });
            entity.setLocalScale(size * aspectRatio, height, size);
            break;

        case 'hexagon': {
            const mesh = createPolygonMesh(app, 6, size / 2, height);
            const meshInstance = new pc.MeshInstance(mesh, material);
            entity.addComponent('render', {
                meshInstances: [meshInstance]
            });
            break;
        }

        case 'octagon': {
            const mesh = createPolygonMesh(app, 8, size / 2, height);
            const meshInstance = new pc.MeshInstance(mesh, material);
            entity.addComponent('render', {
                meshInstances: [meshInstance]
            });
            break;
        }

        case 'oval': {
            const mesh = createOvalMesh(app, (size * aspectRatio) / 2, size / 2, height);
            const meshInstance = new pc.MeshInstance(mesh, material);
            entity.addComponent('render', {
                meshInstances: [meshInstance]
            });
            break;
        }

        case 'circle':
        default:
            entity.addComponent('render', {
                type: 'cylinder',
                material: material
            });
            entity.setLocalScale(size, height, size);
            break;
    }

    return entity;
}

// Get arena boundary for collision checking
export function getArenaBounds(theme) {
    const shapeConfig = ARENA_SHAPES[theme] || ARENA_SHAPES.tech;
    const size = BASE_ARENA_SIZE * shapeConfig.size;
    const aspectRatio = shapeConfig.aspectRatio || 1.0;

    return {
        shape: shapeConfig.shape,
        size: size,
        radiusX: shapeConfig.shape === 'rectangle' || shapeConfig.shape === 'oval'
            ? (size * aspectRatio) / 2 : size / 2,
        radiusZ: size / 2,
        aspectRatio: aspectRatio
    };
}

// Get pillar positions based on arena shape
function getPillarPositions(shapeConfig) {
    const size = BASE_ARENA_SIZE * shapeConfig.size;
    const aspectRatio = shapeConfig.aspectRatio || 1.0;
    const offset = size * 0.38; // Position pillars at ~76% of radius

    switch (shapeConfig.shape) {
        case 'square':
        case 'rectangle': {
            const offsetX = shapeConfig.shape === 'rectangle' ? offset * aspectRatio : offset;
            return [
                [-offsetX, 0, -offset],
                [offsetX, 0, -offset],
                [-offsetX, 0, offset],
                [offsetX, 0, offset]
            ];
        }
        case 'hexagon':
            // 6 pillars at hexagon vertices
            return Array.from({ length: 6 }, (_, i) => {
                const angle = (i / 6) * Math.PI * 2 + Math.PI / 6;
                return [Math.cos(angle) * offset, 0, Math.sin(angle) * offset];
            });
        case 'octagon':
            // 8 pillars at octagon vertices
            return Array.from({ length: 8 }, (_, i) => {
                const angle = (i / 8) * Math.PI * 2 + Math.PI / 8;
                return [Math.cos(angle) * offset, 0, Math.sin(angle) * offset];
            });
        case 'oval': {
            const offsetX = offset * aspectRatio;
            return [
                [-offsetX, 0, 0],
                [offsetX, 0, 0],
                [0, 0, -offset],
                [0, 0, offset],
                [-offsetX * 0.7, 0, -offset * 0.7],
                [offsetX * 0.7, 0, -offset * 0.7],
                [-offsetX * 0.7, 0, offset * 0.7],
                [offsetX * 0.7, 0, offset * 0.7]
            ];
        }
        case 'circle':
        default:
            return [[-offset, 0, -offset], [offset, 0, -offset], [-offset, 0, offset], [offset, 0, offset]];
    }
}

// Create edge decoration based on arena shape
function createEdgeDecoration(app, shapeConfig, config, arenaSize) {
    const radius = arenaSize / 2;
    const aspectRatio = shapeConfig.aspectRatio || 1.0;

    switch (shapeConfig.shape) {
        case 'square':
        case 'rectangle': {
            // Create glowing edge lines for rectangular/square arenas
            const halfX = shapeConfig.shape === 'rectangle' ? radius * aspectRatio : radius;
            const halfZ = radius;
            const edgePositions = [
                { x: 0, z: -halfZ, rotY: 0, scaleX: halfX * 2 },
                { x: 0, z: halfZ, rotY: 0, scaleX: halfX * 2 },
                { x: -halfX, z: 0, rotY: 90, scaleX: halfZ * 2 },
                { x: halfX, z: 0, rotY: 90, scaleX: halfZ * 2 }
            ];
            edgePositions.forEach((edge, i) => {
                const edgeLine = new pc.Entity(`Edge${i}`);
                edgeLine.addComponent('render', {
                    type: 'box',
                    material: createGlowMaterial(config.edgeGlowColor, 0.8)
                });
                edgeLine.setPosition(edge.x, 0.05, edge.z);
                edgeLine.setLocalScale(edge.scaleX, 0.1, 0.3);
                edgeLine.setLocalEulerAngles(0, edge.rotY, 0);
                app.root.addChild(edgeLine);
                arenaEntities.push(edgeLine);
            });
            break;
        }
        case 'hexagon':
        case 'octagon': {
            // Create glowing edges at polygon vertices
            const sides = shapeConfig.shape === 'hexagon' ? 6 : 8;
            const angleOffset = shapeConfig.shape === 'hexagon' ? Math.PI / 6 : Math.PI / 8;
            for (let i = 0; i < sides; i++) {
                const angle1 = (i / sides) * Math.PI * 2 + angleOffset;
                const angle2 = ((i + 1) / sides) * Math.PI * 2 + angleOffset;
                const x1 = Math.cos(angle1) * radius;
                const z1 = Math.sin(angle1) * radius;
                const x2 = Math.cos(angle2) * radius;
                const z2 = Math.sin(angle2) * radius;
                const midX = (x1 + x2) / 2;
                const midZ = (z1 + z2) / 2;
                const length = Math.sqrt((x2 - x1) ** 2 + (z2 - z1) ** 2);
                const edgeAngle = Math.atan2(z2 - z1, x2 - x1) * (180 / Math.PI);

                const edgeLine = new pc.Entity(`Edge${i}`);
                edgeLine.addComponent('render', {
                    type: 'box',
                    material: createGlowMaterial(config.edgeGlowColor, 0.8)
                });
                edgeLine.setPosition(midX, 0.05, midZ);
                edgeLine.setLocalScale(length, 0.1, 0.3);
                edgeLine.setLocalEulerAngles(0, -edgeAngle, 0);
                app.root.addChild(edgeLine);
                arenaEntities.push(edgeLine);
            }
            break;
        }
        case 'oval': {
            // Create torus-like edge for oval (stretched)
            const edgeRing = new pc.Entity('EdgeRing');
            edgeRing.addComponent('render', {
                type: 'torus',
                material: createGlowMaterial(config.edgeGlowColor, 0.8)
            });
            edgeRing.setPosition(0, 0.05, 0);
            edgeRing.setLocalScale(radius * aspectRatio, radius, 0.3);
            edgeRing.setLocalEulerAngles(90, 0, 0);
            app.root.addChild(edgeRing);
            arenaEntities.push(edgeRing);
            break;
        }
        case 'circle':
        default: {
            // Standard torus edge ring for circles
            const edgeRing = new pc.Entity('EdgeRing');
            edgeRing.addComponent('render', {
                type: 'torus',
                material: createGlowMaterial(config.edgeGlowColor, 0.8)
            });
            edgeRing.setPosition(0, 0.05, 0);
            edgeRing.setLocalScale(radius, radius, 0.3);
            edgeRing.setLocalEulerAngles(90, 0, 0);
            app.root.addChild(edgeRing);
            arenaEntities.push(edgeRing);
            break;
        }
    }
}

// Clear existing arena
export function clearArena() {
    arenaEntities.forEach(entity => {
        if (entity && !entity.destroyed) {
            entity.destroy();
        }
    });
    arenaEntities = [];
    collisionObjects = [];
}

// Get collision objects
export function getCollisionObjects() {
    return collisionObjects;
}

// Arena theme configurations
function getArenaConfig(theme) {
    const configs = {
        tech: {
            skyColor: new pc.Color(0.05, 0.05, 0.15),
            floorColor: new pc.Color(0.12, 0.12, 0.15),
            innerFloorColor: new pc.Color(0.18, 0.18, 0.22),
            edgeGlowColor: new pc.Color(0, 0.7, 1),
            centerGlowColor: new pc.Color(1, 0.3, 0.1),
            pillarColor: new pc.Color(0.08, 0.08, 0.1),
            pillarLightColor: new pc.Color(0, 0.8, 1),
            pillarBaseColor: new pc.Color(0.15, 0.15, 0.18),
            bgColor: new pc.Color(0.03, 0.03, 0.08)
        },
        ice: {
            skyColor: new pc.Color(0.1, 0.15, 0.25),
            floorColor: new pc.Color(0.15, 0.2, 0.3),
            innerFloorColor: new pc.Color(0.4, 0.5, 0.6),
            edgeGlowColor: new pc.Color(0.3, 0.8, 1),
            centerGlowColor: new pc.Color(0.5, 0.9, 1),
            pillarColor: new pc.Color(0.2, 0.25, 0.35),
            pillarLightColor: new pc.Color(0.5, 0.8, 1),
            pillarBaseColor: new pc.Color(0.3, 0.35, 0.45),
            bgColor: new pc.Color(0.1, 0.12, 0.18)
        },
        fire: {
            skyColor: new pc.Color(0.15, 0.05, 0.02),
            floorColor: new pc.Color(0.2, 0.08, 0.05),
            innerFloorColor: new pc.Color(0.15, 0.05, 0.02),
            edgeGlowColor: new pc.Color(1, 0.4, 0),
            centerGlowColor: new pc.Color(1, 0.2, 0),
            pillarColor: new pc.Color(0.12, 0.04, 0.02),
            pillarLightColor: new pc.Color(1, 0.5, 0.1),
            pillarBaseColor: new pc.Color(0.18, 0.08, 0.04),
            bgColor: new pc.Color(0.08, 0.02, 0.01)
        },
        storm: {
            skyColor: new pc.Color(0.08, 0.1, 0.18),
            floorColor: new pc.Color(0.1, 0.12, 0.18),
            innerFloorColor: new pc.Color(0.15, 0.18, 0.25),
            edgeGlowColor: new pc.Color(0.4, 0.7, 1),
            centerGlowColor: new pc.Color(0.8, 0.9, 1),
            pillarColor: new pc.Color(0.08, 0.1, 0.15),
            pillarLightColor: new pc.Color(0.6, 0.85, 1),
            pillarBaseColor: new pc.Color(0.12, 0.14, 0.2),
            bgColor: new pc.Color(0.04, 0.05, 0.1)
        },
        void: {
            skyColor: new pc.Color(0.02, 0.0, 0.05),
            floorColor: new pc.Color(0.05, 0.02, 0.08),
            innerFloorColor: new pc.Color(0.08, 0.03, 0.12),
            edgeGlowColor: new pc.Color(0.6, 0.1, 0.9),
            centerGlowColor: new pc.Color(0.9, 0.3, 1),
            pillarColor: new pc.Color(0.03, 0.01, 0.05),
            pillarLightColor: new pc.Color(0.7, 0.2, 1),
            pillarBaseColor: new pc.Color(0.06, 0.02, 0.1),
            bgColor: new pc.Color(0.01, 0.0, 0.03)
        },
        crystal: {
            skyColor: new pc.Color(0.05, 0.12, 0.1),
            floorColor: new pc.Color(0.08, 0.15, 0.12),
            innerFloorColor: new pc.Color(0.12, 0.25, 0.2),
            edgeGlowColor: new pc.Color(0.3, 1, 0.8),
            centerGlowColor: new pc.Color(0.5, 1, 0.9),
            pillarColor: new pc.Color(0.1, 0.2, 0.18),
            pillarLightColor: new pc.Color(0.4, 1, 0.7),
            pillarBaseColor: new pc.Color(0.15, 0.25, 0.2),
            bgColor: new pc.Color(0.02, 0.08, 0.06)
        },
        jungle: {
            skyColor: new pc.Color(0.05, 0.1, 0.02),
            floorColor: new pc.Color(0.1, 0.15, 0.05),
            innerFloorColor: new pc.Color(0.15, 0.22, 0.08),
            edgeGlowColor: new pc.Color(0.4, 1, 0.2),
            centerGlowColor: new pc.Color(0.6, 1, 0.3),
            pillarColor: new pc.Color(0.2, 0.15, 0.08),
            pillarLightColor: new pc.Color(0.5, 1, 0.3),
            pillarBaseColor: new pc.Color(0.15, 0.1, 0.05),
            bgColor: new pc.Color(0.02, 0.05, 0.01)
        },
        ocean: {
            skyColor: new pc.Color(0.02, 0.08, 0.15),
            floorColor: new pc.Color(0.05, 0.12, 0.2),
            innerFloorColor: new pc.Color(0.08, 0.18, 0.28),
            edgeGlowColor: new pc.Color(0.2, 0.6, 1),
            centerGlowColor: new pc.Color(0.3, 0.8, 1),
            pillarColor: new pc.Color(0.04, 0.1, 0.18),
            pillarLightColor: new pc.Color(0.3, 0.7, 1),
            pillarBaseColor: new pc.Color(0.06, 0.12, 0.2),
            bgColor: new pc.Color(0.01, 0.04, 0.1)
        },
        solar: {
            skyColor: new pc.Color(0.2, 0.12, 0.02),
            floorColor: new pc.Color(0.25, 0.15, 0.05),
            innerFloorColor: new pc.Color(0.35, 0.22, 0.08),
            edgeGlowColor: new pc.Color(1, 0.8, 0.2),
            centerGlowColor: new pc.Color(1, 0.9, 0.4),
            pillarColor: new pc.Color(0.3, 0.2, 0.08),
            pillarLightColor: new pc.Color(1, 0.85, 0.3),
            pillarBaseColor: new pc.Color(0.25, 0.18, 0.06),
            bgColor: new pc.Color(0.15, 0.08, 0.02)
        },
        nightmare: {
            skyColor: new pc.Color(0.08, 0.0, 0.08),
            floorColor: new pc.Color(0.1, 0.02, 0.1),
            innerFloorColor: new pc.Color(0.15, 0.03, 0.15),
            edgeGlowColor: new pc.Color(1, 0, 0.5),
            centerGlowColor: new pc.Color(1, 0.2, 0.6),
            pillarColor: new pc.Color(0.08, 0.01, 0.08),
            pillarLightColor: new pc.Color(1, 0.1, 0.5),
            pillarBaseColor: new pc.Color(0.12, 0.02, 0.12),
            bgColor: new pc.Color(0.04, 0.0, 0.04)
        }
    };
    return configs[theme] || configs.tech;
}

// Create the arena for a specific theme
export function createArena(app, camera, theme = 'tech') {
    clearArena();

    const config = getArenaConfig(theme);
    const shapeConfig = ARENA_SHAPES[theme] || ARENA_SHAPES.tech;
    const arenaSize = BASE_ARENA_SIZE * shapeConfig.size;
    const innerSize = arenaSize * shapeConfig.innerSize;

    // Update camera clear color for theme
    camera.camera.clearColor = config.skyColor;

    // Main platform - using shape-based floor
    const ground = createFloorEntity(app, 'Ground', shapeConfig, BASE_ARENA_SIZE, 0.6, createMetalMaterial(config.floorColor));
    ground.setPosition(0, -0.3, 0);
    app.root.addChild(ground);
    arenaEntities.push(ground);

    // Inner floor - same shape, smaller
    const innerShapeConfig = { ...shapeConfig, size: shapeConfig.size * shapeConfig.innerSize };
    const innerFloor = createFloorEntity(app, 'InnerFloor', innerShapeConfig, BASE_ARENA_SIZE, 0.1, createMetalMaterial(config.innerFloorColor));
    innerFloor.setPosition(0, 0, 0);
    app.root.addChild(innerFloor);
    arenaEntities.push(innerFloor);

    // Edge decoration based on shape
    createEdgeDecoration(app, shapeConfig, config, arenaSize);

    // Center ring (always circular)
    const centerRing = new pc.Entity('CenterRing');
    centerRing.addComponent('render', {
        type: 'torus',
        material: createGlowMaterial(config.centerGlowColor, 0.5)
    });
    centerRing.setPosition(0, 0.08, 0);
    const centerScale = 4 * shapeConfig.size;
    centerRing.setLocalScale(centerScale, centerScale, 0.15);
    centerRing.setLocalEulerAngles(90, 0, 0);
    app.root.addChild(centerRing);
    arenaEntities.push(centerRing);

    // Pillars based on arena shape
    const pillarPositions = getPillarPositions(shapeConfig);

    pillarPositions.forEach((pos, i) => {
        const pillar = new pc.Entity(`Pillar${i}`);
        pillar.addComponent('render', {
            type: 'cylinder',
            material: createMetalMaterial(config.pillarColor)
        });
        pillar.setPosition(pos[0], 2, pos[2]);
        pillar.setLocalScale(1.2, 4, 1.2);
        app.root.addChild(pillar);
        arenaEntities.push(pillar);

        // Register pillar for collision
        collisionObjects.push({
            x: pos[0],
            z: pos[2],
            radius: 0.8,
            type: 'pillar'
        });

        const pillarLight = new pc.Entity(`PillarLight${i}`);
        pillarLight.addComponent('render', {
            type: 'sphere',
            material: createGlowMaterial(config.pillarLightColor, 1.2)
        });
        pillarLight.setPosition(pos[0], 4.2, pos[2]);
        pillarLight.setLocalScale(0.6, 0.6, 0.6);
        app.root.addChild(pillarLight);
        arenaEntities.push(pillarLight);

        const pillarBase = new pc.Entity(`PillarBase${i}`);
        pillarBase.addComponent('render', {
            type: 'cylinder',
            material: createMetalMaterial(config.pillarBaseColor)
        });
        pillarBase.setPosition(pos[0], 0.2, pos[2]);
        pillarBase.setLocalScale(1.8, 0.4, 1.8);
        app.root.addChild(pillarBase);
        arenaEntities.push(pillarBase);
    });

    // Jumpable platforms (scaled for arena size)
    createPlatforms(app, theme, config, shapeConfig);

    // Theme-specific decorations (scaled for arena size)
    createThemeDecorations(app, theme, config, shapeConfig);

    // Background structures
    for (let i = -3; i <= 3; i++) {
        const height = 8 + Math.random() * 12;
        const bgBuilding = new pc.Entity(`BgBuilding${i}`);
        bgBuilding.addComponent('render', {
            type: 'box',
            material: createMaterial(config.bgColor, 0.5, 0.3)
        });
        bgBuilding.setPosition(i * 8, height / 2 - 2, -25);
        bgBuilding.setLocalScale(3 + Math.random() * 2, height, 3);
        app.root.addChild(bgBuilding);
        arenaEntities.push(bgBuilding);
    }

    console.log(`${theme.toUpperCase()} arena created`);
    return collisionObjects;
}

// Create platforms for the arena
function createPlatforms(app, theme, config, shapeConfig) {
    const scale = shapeConfig.size;
    const aspectRatio = shapeConfig.aspectRatio || 1.0;

    // Platform configs - positions will be scaled by arena size
    const platformConfigs = {
        tech: [
            { x: -4, z: 0, height: 0.8, radius: 1.5 },
            { x: 4, z: 0, height: 0.8, radius: 1.5 },
            { x: 0, z: -4, height: 1.2, radius: 1.2 },
        ],
        ice: [
            { x: -6, z: -4, height: 0.6, radius: 1.8 },
            { x: 6, z: -4, height: 0.6, radius: 1.8 },
            { x: 0, z: 5, height: 1.0, radius: 1.5 },
            { x: -4, z: 4, height: 0.4, radius: 1.2 },
            { x: 4, z: 4, height: 0.4, radius: 1.2 },
            { x: 0, z: 0, height: 0.5, radius: 2.0 },
        ],
        fire: [
            { x: -5, z: -3, height: 1.0, radius: 1.3 },
            { x: 5, z: -3, height: 1.0, radius: 1.3 },
            { x: 0, z: 4, height: 1.5, radius: 1.0 },
            { x: -3, z: 2, height: 0.7, radius: 1.2 },
            { x: 3, z: 2, height: 0.7, radius: 1.2 },
        ],
        storm: [
            { x: -7, z: 0, height: 0.7, radius: 1.4 },
            { x: 7, z: 0, height: 0.7, radius: 1.4 },
            { x: -4, z: -5, height: 1.1, radius: 1.3 },
            { x: 4, z: -5, height: 1.1, radius: 1.3 },
            { x: 0, z: 5, height: 0.5, radius: 2.0 },
            { x: 0, z: 0, height: 0.8, radius: 1.6 },
        ],
        void: [
            { x: -6, z: -6, height: 1.2, radius: 1.2 },
            { x: 6, z: -6, height: 1.2, radius: 1.2 },
            { x: -6, z: 6, height: 0.8, radius: 1.2 },
            { x: 6, z: 6, height: 0.8, radius: 1.2 },
            { x: 0, z: 0, height: 0.5, radius: 2.5 },
            { x: -3, z: 0, height: 1.0, radius: 1.4 },
            { x: 3, z: 0, height: 1.0, radius: 1.4 },
        ],
        crystal: [
            { x: -6, z: -3, height: 1.0, radius: 1.4 },
            { x: 6, z: -3, height: 1.0, radius: 1.4 },
            { x: 0, z: 5, height: 1.4, radius: 1.3 },
            { x: -4, z: 3, height: 0.6, radius: 1.0 },
            { x: 4, z: 3, height: 0.6, radius: 1.0 },
            { x: 0, z: -1, height: 0.8, radius: 1.5 },
        ],
        jungle: [
            { x: -5, z: -4, height: 0.8, radius: 1.6 },
            { x: 5, z: -4, height: 0.8, radius: 1.6 },
            { x: -3, z: 4, height: 1.2, radius: 1.2 },
            { x: 3, z: 4, height: 1.2, radius: 1.2 },
            { x: 0, z: 0, height: 0.4, radius: 2.0 },
            { x: -7, z: 0, height: 0.6, radius: 1.3 },
            { x: 7, z: 0, height: 0.6, radius: 1.3 },
        ],
        ocean: [
            { x: -6, z: 0, height: 0.5, radius: 1.8 },
            { x: 6, z: 0, height: 0.5, radius: 1.8 },
            { x: 0, z: -6, height: 0.8, radius: 1.5 },
            { x: 0, z: 6, height: 0.8, radius: 1.5 },
            { x: -4, z: -4, height: 0.6, radius: 1.3 },
            { x: 4, z: 4, height: 0.6, radius: 1.3 },
        ],
        solar: [
            { x: -5, z: -5, height: 1.0, radius: 1.3 },
            { x: 5, z: -5, height: 1.0, radius: 1.3 },
            { x: -5, z: 5, height: 1.0, radius: 1.3 },
            { x: 5, z: 5, height: 1.0, radius: 1.3 },
            { x: 0, z: 0, height: 1.5, radius: 1.8 },
            { x: -3, z: 0, height: 0.7, radius: 1.2 },
            { x: 3, z: 0, height: 0.7, radius: 1.2 },
        ],
        nightmare: [
            { x: -7, z: -4, height: 1.2, radius: 1.2 },
            { x: 7, z: -4, height: 1.2, radius: 1.2 },
            { x: -7, z: 4, height: 0.8, radius: 1.2 },
            { x: 7, z: 4, height: 0.8, radius: 1.2 },
            { x: 0, z: -7, height: 1.0, radius: 1.4 },
            { x: 0, z: 7, height: 1.0, radius: 1.4 },
            { x: 0, z: 0, height: 0.6, radius: 2.2 },
            { x: -4, z: 0, height: 0.9, radius: 1.3 },
            { x: 4, z: 0, height: 0.9, radius: 1.3 },
        ]
    };

    const platforms = platformConfigs[theme] || platformConfigs.tech;

    platforms.forEach((p, i) => {
        // Scale position based on arena size, with aspect ratio for x in rectangular/oval arenas
        const scaledX = p.x * scale * (shapeConfig.shape === 'rectangle' || shapeConfig.shape === 'oval' ? aspectRatio : 1);
        const scaledZ = p.z * scale;

        // Main platform
        const platform = new pc.Entity(`Platform${i}`);
        platform.addComponent('render', {
            type: 'cylinder',
            material: createMetalMaterial(config.pillarBaseColor)
        });
        platform.setPosition(scaledX, p.height / 2, scaledZ);
        platform.setLocalScale(p.radius * 2, p.height, p.radius * 2);
        app.root.addChild(platform);
        arenaEntities.push(platform);

        // Glowing top edge
        const platformTop = new pc.Entity(`PlatformTop${i}`);
        platformTop.addComponent('render', {
            type: 'torus',
            material: createGlowMaterial(config.edgeGlowColor, 0.5)
        });
        platformTop.setPosition(scaledX, p.height + 0.05, scaledZ);
        platformTop.setLocalScale(p.radius, p.radius, 0.1);
        platformTop.setLocalEulerAngles(90, 0, 0);
        app.root.addChild(platformTop);
        arenaEntities.push(platformTop);

        // Register platform for collision
        collisionObjects.push({
            x: scaledX,
            z: scaledZ,
            radius: p.radius,
            type: 'platform',
            y: p.height,
            height: p.height
        });
    });
}

// Create theme-specific decorations
function createThemeDecorations(app, theme, config, shapeConfig) {
    const scale = shapeConfig.size;
    const aspectRatio = shapeConfig.aspectRatio || 1.0;

    // Helper to scale coordinates
    const scaleX = (x) => x * scale * (shapeConfig.shape === 'rectangle' || shapeConfig.shape === 'oval' ? aspectRatio : 1);
    const scaleZ = (z) => z * scale;
    const scaleRadius = (r) => r * scale;

    if (theme === 'ice') {
        // Ice crystals - hazards with collision
        const crystalCount = shapeConfig.shape === 'hexagon' ? 6 : 8;
        for (let i = 0; i < crystalCount; i++) {
            const angle = (i / crystalCount) * Math.PI * 2;
            const baseRadius = 9;
            const x = Math.cos(angle) * scaleRadius(baseRadius);
            const z = Math.sin(angle) * scaleRadius(baseRadius);

            const crystal = new pc.Entity(`Crystal${i}`);
            crystal.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(0.6, 0.85, 1), 0.4)
            });
            crystal.setPosition(x, 1.5, z);
            crystal.setLocalScale(0.5, 3, 0.5);
            crystal.setLocalEulerAngles(0, 0, (Math.random() - 0.5) * 20);
            app.root.addChild(crystal);
            arenaEntities.push(crystal);

            // Add hazard collision
            collisionObjects.push({
                x: x,
                z: z,
                radius: 0.4,
                type: 'hazard',
                damage: 10,
                name: 'Ice Crystal'
            });
        }

        // Snowflake particles (no collision)
        for (let i = 0; i < 20; i++) {
            const flake = new pc.Entity(`Snowflake${i}`);
            flake.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(1, 1, 1), 0.6)
            });
            flake.setPosition(
                (Math.random() - 0.5) * 20,
                Math.random() * 8 + 1,
                (Math.random() - 0.5) * 20
            );
            flake.setLocalScale(0.1, 0.1, 0.1);
            app.root.addChild(flake);
            arenaEntities.push(flake);
        }
    }

    if (theme === 'fire') {
        // Lava pools around edge - hazards (octagonal arena)
        const poolCount = shapeConfig.shape === 'octagon' ? 8 : 4;
        for (let i = 0; i < poolCount; i++) {
            const angle = (i / poolCount) * Math.PI * 2 + Math.PI / poolCount;
            const x = Math.cos(angle) * scaleRadius(9);
            const z = Math.sin(angle) * scaleRadius(9);

            const lavaPool = new pc.Entity(`LavaPool${i}`);
            lavaPool.addComponent('render', {
                type: 'cylinder',
                material: createGlowMaterial(new pc.Color(1, 0.3, 0), 1.2)
            });
            lavaPool.setPosition(x, 0.05, z);
            lavaPool.setLocalScale(2, 0.1, 2);
            app.root.addChild(lavaPool);
            arenaEntities.push(lavaPool);

            // Add lava hazard (larger radius, floor-based)
            collisionObjects.push({
                x: x,
                z: z,
                radius: 1.0,
                type: 'hazard',
                damage: 15,
                name: 'Lava Pool',
                isFloor: true
            });
        }

        // Fire pillars - hazards with collision
        const pillarCount = shapeConfig.shape === 'octagon' ? 4 : 3;
        for (let i = 0; i < pillarCount; i++) {
            const angle = (i / pillarCount) * Math.PI * 2;
            const x = Math.cos(angle) * scaleRadius(6);
            const z = Math.sin(angle) * scaleRadius(6);

            const firePillar = new pc.Entity(`FirePillar${i}`);
            firePillar.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(1, 0.5, 0.1), 0.8)
            });
            firePillar.setPosition(x, 1, z);
            firePillar.setLocalScale(0.3, 2, 0.3);
            app.root.addChild(firePillar);
            arenaEntities.push(firePillar);

            // Add fire pillar hazard
            collisionObjects.push({
                x: x,
                z: z,
                radius: 0.3,
                type: 'hazard',
                damage: 12,
                name: 'Fire Pillar'
            });
        }

        // Embers (no collision) - spread across larger arena
        const emberSpread = 18 * scale;
        for (let i = 0; i < 20; i++) {
            const ember = new pc.Entity(`Ember${i}`);
            ember.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(1, 0.6, 0.1), 1.0)
            });
            ember.setPosition(
                (Math.random() - 0.5) * emberSpread,
                Math.random() * 6 + 1,
                (Math.random() - 0.5) * emberSpread
            );
            ember.setLocalScale(0.08, 0.08, 0.08);
            app.root.addChild(ember);
            arenaEntities.push(ember);
        }
    }

    if (theme === 'storm') {
        // Lightning rods - hazards with collision (rectangular arena)
        // Place rods along the edges of the rectangular arena
        const rodPositions = [
            { x: scaleX(-8), z: scaleZ(-5) },
            { x: scaleX(8), z: scaleZ(-5) },
            { x: scaleX(-8), z: scaleZ(5) },
            { x: scaleX(8), z: scaleZ(5) },
            { x: scaleX(-10), z: 0 },
            { x: scaleX(10), z: 0 },
            { x: 0, z: scaleZ(-6) },
            { x: 0, z: scaleZ(6) }
        ];

        rodPositions.forEach((pos, i) => {
            const rod = new pc.Entity(`LightningRod${i}`);
            rod.addComponent('render', {
                type: 'cylinder',
                material: createMetalMaterial(new pc.Color(0.3, 0.35, 0.4))
            });
            rod.setPosition(pos.x, 2, pos.z);
            rod.setLocalScale(0.15, 4, 0.15);
            app.root.addChild(rod);
            arenaEntities.push(rod);

            // Glowing tip
            const tip = new pc.Entity(`RodTip${i}`);
            tip.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.5, 0.8, 1), 1.2)
            });
            tip.setPosition(pos.x, 4.2, pos.z);
            tip.setLocalScale(0.3, 0.3, 0.3);
            app.root.addChild(tip);
            arenaEntities.push(tip);

            // Add lightning rod hazard
            collisionObjects.push({
                x: pos.x,
                z: pos.z,
                radius: 0.3,
                type: 'hazard',
                damage: 18,
                name: 'Lightning Rod'
            });
        });

        // Storm clouds (no collision) - spread across larger arena
        const cloudSpreadX = 25 * scale * aspectRatio;
        const cloudSpreadZ = 25 * scale;
        for (let i = 0; i < 12; i++) {
            const cloud = new pc.Entity(`Cloud${i}`);
            cloud.addComponent('render', {
                type: 'sphere',
                material: createMaterial(new pc.Color(0.15, 0.18, 0.25), 0.2, 0.3)
            });
            cloud.setPosition(
                (Math.random() - 0.5) * cloudSpreadX,
                8 + Math.random() * 4,
                (Math.random() - 0.5) * cloudSpreadZ
            );
            cloud.setLocalScale(3 + Math.random() * 2, 1, 2 + Math.random());
            app.root.addChild(cloud);
            arenaEntities.push(cloud);
        }

        // Electric particles (no collision)
        const sparkSpreadX = 16 * scale * aspectRatio;
        const sparkSpreadZ = 16 * scale;
        for (let i = 0; i < 18; i++) {
            const spark = new pc.Entity(`Spark${i}`);
            spark.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.6, 0.9, 1), 1.5)
            });
            spark.setPosition(
                (Math.random() - 0.5) * sparkSpreadX,
                Math.random() * 5 + 1,
                (Math.random() - 0.5) * sparkSpreadZ
            );
            spark.setLocalScale(0.06, 0.06, 0.06);
            app.root.addChild(spark);
            arenaEntities.push(spark);
        }
    }

    if (theme === 'void') {
        // Floating void crystals - hazards (huge circular arena)
        const crystalCount = 12;
        for (let i = 0; i < crystalCount; i++) {
            const angle = (i / crystalCount) * Math.PI * 2;
            const baseRadius = 6 + (i % 2) * 4;
            const x = Math.cos(angle) * scaleRadius(baseRadius);
            const z = Math.sin(angle) * scaleRadius(baseRadius);

            const crystal = new pc.Entity(`VoidCrystal${i}`);
            crystal.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(0.5, 0.1, 0.8), 0.6)
            });
            crystal.setPosition(x, 2 + Math.sin(i) * 0.5, z);
            crystal.setLocalScale(0.4, 1.5, 0.4);
            crystal.setLocalEulerAngles(180, 0, (Math.random() - 0.5) * 30);
            app.root.addChild(crystal);
            arenaEntities.push(crystal);

            // Add void crystal hazard
            collisionObjects.push({
                x: x,
                z: z,
                radius: 0.35,
                type: 'hazard',
                damage: 20,
                name: 'Void Crystal'
            });
        }

        // Dark energy orbs (no collision - they float high)
        const orbSpread = 14 * scale;
        for (let i = 0; i < 8; i++) {
            const orb = new pc.Entity(`VoidOrb${i}`);
            orb.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.3, 0, 0.5), 0.8)
            });
            orb.setPosition(
                (Math.random() - 0.5) * orbSpread,
                3 + Math.random() * 3,
                (Math.random() - 0.5) * orbSpread
            );
            orb.setLocalScale(0.8, 0.8, 0.8);
            app.root.addChild(orb);
            arenaEntities.push(orb);
        }

        // Void particles (no collision)
        const particleSpread = 20 * scale;
        for (let i = 0; i < 40; i++) {
            const particle = new pc.Entity(`VoidParticle${i}`);
            particle.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.7, 0.3, 1), 1.0)
            });
            particle.setPosition(
                (Math.random() - 0.5) * particleSpread,
                Math.random() * 8 + 0.5,
                (Math.random() - 0.5) * particleSpread
            );
            particle.setLocalScale(0.05, 0.05, 0.05);
            app.root.addChild(particle);
            arenaEntities.push(particle);
        }

        // Ominous ring around arena - scaled
        const voidRing = new pc.Entity('VoidRing');
        voidRing.addComponent('render', {
            type: 'torus',
            material: createGlowMaterial(new pc.Color(0.4, 0, 0.6), 0.4)
        });
        voidRing.setPosition(0, 0.5, 0);
        const ringSize = 12 * scale;
        voidRing.setLocalScale(ringSize, ringSize, 1);
        voidRing.setLocalEulerAngles(90, 0, 0);
        app.root.addChild(voidRing);
        arenaEntities.push(voidRing);
    }

    // CRYSTAL ARENA - Glowing gem formations (hexagonal)
    if (theme === 'crystal') {
        // Large crystal formations at hexagon points - hazards
        const crystalCount = 6; // Match hexagon vertices
        for (let i = 0; i < crystalCount; i++) {
            const angle = (i / crystalCount) * Math.PI * 2 + Math.PI / 6;
            const x = Math.cos(angle) * scaleRadius(9);
            const z = Math.sin(angle) * scaleRadius(9);

            const crystal = new pc.Entity(`GemCrystal${i}`);
            crystal.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(0.3, 1, 0.8), 0.6)
            });
            crystal.setPosition(x, 1.5, z);
            crystal.setLocalScale(0.6, 3, 0.6);
            crystal.setLocalEulerAngles(0, 0, (Math.random() - 0.5) * 15);
            app.root.addChild(crystal);
            arenaEntities.push(crystal);

            collisionObjects.push({
                x: x, z: z, radius: 0.5,
                type: 'hazard', damage: 15, name: 'Crystal Spike'
            });
        }

        // Inner ring of smaller crystals
        for (let i = 0; i < 6; i++) {
            const angle = (i / 6) * Math.PI * 2;
            const x = Math.cos(angle) * scaleRadius(5);
            const z = Math.sin(angle) * scaleRadius(5);

            const smallCrystal = new pc.Entity(`SmallCrystal${i}`);
            smallCrystal.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(0.5, 1, 0.9), 0.7)
            });
            smallCrystal.setPosition(x, 1, z);
            smallCrystal.setLocalScale(0.4, 2, 0.4);
            smallCrystal.setLocalEulerAngles(0, 0, (Math.random() - 0.5) * 20);
            app.root.addChild(smallCrystal);
            arenaEntities.push(smallCrystal);
        }

        // Floating gem particles - spread across larger arena
        const gemSpread = 18 * scale;
        for (let i = 0; i < 40; i++) {
            const gem = new pc.Entity(`GemParticle${i}`);
            gem.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.4, 1, 0.9), 0.8)
            });
            gem.setPosition(
                (Math.random() - 0.5) * gemSpread,
                Math.random() * 6 + 1,
                (Math.random() - 0.5) * gemSpread
            );
            gem.setLocalScale(0.08, 0.08, 0.08);
            app.root.addChild(gem);
            arenaEntities.push(gem);
        }
    }

    // JUNGLE ARENA - Trees and vines (oval arena)
    if (theme === 'jungle') {
        // Tree trunks - hazards (thorny) - positioned around oval edge
        const treeCount = 8;
        for (let i = 0; i < treeCount; i++) {
            const angle = (i / treeCount) * Math.PI * 2 + Math.PI / treeCount;
            const x = Math.cos(angle) * scaleX(8);
            const z = Math.sin(angle) * scaleZ(8);

            const trunk = new pc.Entity(`TreeTrunk${i}`);
            trunk.addComponent('render', {
                type: 'cylinder',
                material: createMaterial(new pc.Color(0.3, 0.2, 0.1), 0.3, 0.5)
            });
            trunk.setPosition(x, 2, z);
            trunk.setLocalScale(0.8, 4, 0.8);
            app.root.addChild(trunk);
            arenaEntities.push(trunk);

            // Tree canopy
            const canopy = new pc.Entity(`Canopy${i}`);
            canopy.addComponent('render', {
                type: 'sphere',
                material: createMaterial(new pc.Color(0.2, 0.5, 0.1), 0.2, 0.6)
            });
            canopy.setPosition(x, 4.5, z);
            canopy.setLocalScale(2.5, 2, 2.5);
            app.root.addChild(canopy);
            arenaEntities.push(canopy);

            collisionObjects.push({
                x: x, z: z, radius: 0.6,
                type: 'hazard', damage: 12, name: 'Thorny Tree'
            });
        }

        // Glowing mushrooms - spread across oval
        const mushroomSpreadX = 16 * scale * aspectRatio;
        const mushroomSpreadZ = 16 * scale;
        for (let i = 0; i < 18; i++) {
            const mushroom = new pc.Entity(`Mushroom${i}`);
            mushroom.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.5, 1, 0.3), 0.6)
            });
            mushroom.setPosition(
                (Math.random() - 0.5) * mushroomSpreadX,
                0.2,
                (Math.random() - 0.5) * mushroomSpreadZ
            );
            mushroom.setLocalScale(0.3, 0.2, 0.3);
            app.root.addChild(mushroom);
            arenaEntities.push(mushroom);
        }

        // Fireflies - spread across larger oval
        const fireflySpreadX = 18 * scale * aspectRatio;
        const fireflySpreadZ = 18 * scale;
        for (let i = 0; i < 30; i++) {
            const firefly = new pc.Entity(`Firefly${i}`);
            firefly.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.8, 1, 0.2), 1.2)
            });
            firefly.setPosition(
                (Math.random() - 0.5) * fireflySpreadX,
                Math.random() * 5 + 1,
                (Math.random() - 0.5) * fireflySpreadZ
            );
            firefly.setLocalScale(0.06, 0.06, 0.06);
            app.root.addChild(firefly);
            arenaEntities.push(firefly);
        }
    }

    // OCEAN ARENA - Coral and bubbles (large circular)
    if (theme === 'ocean') {
        // Coral formations - hazards in two rings
        const coralCount = 10;
        for (let i = 0; i < coralCount; i++) {
            const angle = (i / coralCount) * Math.PI * 2;
            const baseRadius = i % 2 === 0 ? 8 : 10;
            const x = Math.cos(angle) * scaleRadius(baseRadius);
            const z = Math.sin(angle) * scaleRadius(baseRadius);

            const coral = new pc.Entity(`Coral${i}`);
            coral.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(1, 0.4, 0.6), 0.4)
            });
            coral.setPosition(x, 1, z);
            coral.setLocalScale(0.8, 2, 0.8);
            app.root.addChild(coral);
            arenaEntities.push(coral);

            collisionObjects.push({
                x: x, z: z, radius: 0.5,
                type: 'hazard', damage: 18, name: 'Sharp Coral'
            });
        }

        // Bubble particles - spread across larger arena
        const bubbleSpread = 20 * scale;
        for (let i = 0; i < 50; i++) {
            const bubble = new pc.Entity(`Bubble${i}`);
            bubble.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(0.5, 0.8, 1), 0.3)
            });
            bubble.setPosition(
                (Math.random() - 0.5) * bubbleSpread,
                Math.random() * 8 + 0.5,
                (Math.random() - 0.5) * bubbleSpread
            );
            const size = 0.1 + Math.random() * 0.15;
            bubble.setLocalScale(size, size, size);
            app.root.addChild(bubble);
            arenaEntities.push(bubble);
        }

        // Kelp strands - spread across larger arena
        const kelpSpread = 16 * scale;
        for (let i = 0; i < 15; i++) {
            const kelp = new pc.Entity(`Kelp${i}`);
            kelp.addComponent('render', {
                type: 'cylinder',
                material: createMaterial(new pc.Color(0.1, 0.4, 0.2), 0.1, 0.5)
            });
            kelp.setPosition(
                (Math.random() - 0.5) * kelpSpread,
                2,
                (Math.random() - 0.5) * kelpSpread
            );
            kelp.setLocalScale(0.1, 4, 0.1);
            kelp.setLocalEulerAngles((Math.random() - 0.5) * 20, 0, (Math.random() - 0.5) * 20);
            app.root.addChild(kelp);
            arenaEntities.push(kelp);
        }
    }

    // SOLAR ARENA - Sun rays and flames (octagonal)
    if (theme === 'solar') {
        // Solar pillars at octagon vertices - hazards (burning hot)
        const pillarCount = 8;
        for (let i = 0; i < pillarCount; i++) {
            const angle = (i / pillarCount) * Math.PI * 2 + Math.PI / pillarCount;
            const x = Math.cos(angle) * scaleRadius(8);
            const z = Math.sin(angle) * scaleRadius(8);

            const pillar = new pc.Entity(`SolarPillar${i}`);
            pillar.addComponent('render', {
                type: 'cylinder',
                material: createGlowMaterial(new pc.Color(1, 0.7, 0.2), 0.8)
            });
            pillar.setPosition(x, 2, z);
            pillar.setLocalScale(0.4, 4, 0.4);
            app.root.addChild(pillar);
            arenaEntities.push(pillar);

            // Flame top
            const flame = new pc.Entity(`Flame${i}`);
            flame.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(1, 0.5, 0), 1.2)
            });
            flame.setPosition(x, 4.5, z);
            flame.setLocalScale(0.6, 1.2, 0.6);
            app.root.addChild(flame);
            arenaEntities.push(flame);

            collisionObjects.push({
                x: x, z: z, radius: 0.4,
                type: 'hazard', damage: 22, name: 'Solar Pillar'
            });
        }

        // Sun particles - spread across larger arena
        const sparkSpread = 18 * scale;
        for (let i = 0; i < 35; i++) {
            const spark = new pc.Entity(`SunSpark${i}`);
            spark.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(1, 0.9, 0.3), 1.5)
            });
            spark.setPosition(
                (Math.random() - 0.5) * sparkSpread,
                Math.random() * 6 + 1,
                (Math.random() - 0.5) * sparkSpread
            );
            spark.setLocalScale(0.07, 0.07, 0.07);
            app.root.addChild(spark);
            arenaEntities.push(spark);
        }

        // Central sun orb - slightly larger for bigger arena
        const sunOrb = new pc.Entity('SunOrb');
        sunOrb.addComponent('render', {
            type: 'sphere',
            material: createGlowMaterial(new pc.Color(1, 0.85, 0.4), 1.5)
        });
        sunOrb.setPosition(0, 8, 0);
        const sunSize = 3 * Math.sqrt(scale);
        sunOrb.setLocalScale(sunSize, sunSize, sunSize);
        app.root.addChild(sunOrb);
        arenaEntities.push(sunOrb);
    }

    // NIGHTMARE ARENA - Dark tendrils and eyes (huge square)
    if (theme === 'nightmare') {
        // Shadow tendrils - hazards along edges of square
        const tendrilPositions = [
            // Corners
            { x: scaleX(-9), z: scaleZ(-9) },
            { x: scaleX(9), z: scaleZ(-9) },
            { x: scaleX(-9), z: scaleZ(9) },
            { x: scaleX(9), z: scaleZ(9) },
            // Edge midpoints
            { x: scaleX(-9), z: 0 },
            { x: scaleX(9), z: 0 },
            { x: 0, z: scaleZ(-9) },
            { x: 0, z: scaleZ(9) },
            // Inner ring
            { x: scaleX(-5), z: scaleZ(-5) },
            { x: scaleX(5), z: scaleZ(-5) },
            { x: scaleX(-5), z: scaleZ(5) },
            { x: scaleX(5), z: scaleZ(5) },
        ];

        tendrilPositions.forEach((pos, i) => {
            const tendril = new pc.Entity(`Tendril${i}`);
            tendril.addComponent('render', {
                type: 'cone',
                material: createGlowMaterial(new pc.Color(0.5, 0, 0.3), 0.4)
            });
            tendril.setPosition(pos.x, 1.5, pos.z);
            tendril.setLocalScale(0.3, 3, 0.3);
            tendril.setLocalEulerAngles(0, 0, (Math.random() - 0.5) * 30);
            app.root.addChild(tendril);
            arenaEntities.push(tendril);

            collisionObjects.push({
                x: pos.x, z: pos.z, radius: 0.35,
                type: 'hazard', damage: 25, name: 'Shadow Tendril'
            });
        });

        // Floating eyes - spread across huge arena
        const eyeSpread = 16 * scale;
        for (let i = 0; i < 12; i++) {
            const eye = new pc.Entity(`NightmareEye${i}`);
            eye.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(1, 0, 0.4), 1.0)
            });
            eye.setPosition(
                (Math.random() - 0.5) * eyeSpread,
                3 + Math.random() * 3,
                (Math.random() - 0.5) * eyeSpread
            );
            eye.setLocalScale(0.4, 0.5, 0.3);
            app.root.addChild(eye);
            arenaEntities.push(eye);

            // Pupil
            const pupil = new pc.Entity(`Pupil${i}`);
            pupil.addComponent('render', {
                type: 'sphere',
                material: createMaterial(new pc.Color(0.1, 0, 0.1), 0, 0.9)
            });
            pupil.setLocalPosition(0, 0, -0.15);
            pupil.setLocalScale(0.4, 0.5, 0.3);
            eye.addChild(pupil);
        }

        // Dark particles - spread across huge square arena
        const particleSpread = 20 * scale;
        for (let i = 0; i < 50; i++) {
            const particle = new pc.Entity(`DarkParticle${i}`);
            particle.addComponent('render', {
                type: 'sphere',
                material: createGlowMaterial(new pc.Color(1, 0.2, 0.6), 0.8)
            });
            particle.setPosition(
                (Math.random() - 0.5) * particleSpread,
                Math.random() * 7 + 0.5,
                (Math.random() - 0.5) * particleSpread
            );
            particle.setLocalScale(0.05, 0.05, 0.05);
            app.root.addChild(particle);
            arenaEntities.push(particle);
        }

        // Nightmare ring - scaled for square arena
        const nightmareRing = new pc.Entity('NightmareRing');
        nightmareRing.addComponent('render', {
            type: 'torus',
            material: createGlowMaterial(new pc.Color(1, 0, 0.5), 0.5)
        });
        nightmareRing.setPosition(0, 0.3, 0);
        const ringSize = 10 * scale;
        nightmareRing.setLocalScale(ringSize, ringSize, 0.8);
        nightmareRing.setLocalEulerAngles(90, 0, 0);
        app.root.addChild(nightmareRing);
        arenaEntities.push(nightmareRing);
    }
}
